import React, { useState } from 'react'
import {
	createColumnHelper,
	flexRender,
	getCoreRowModel,
	useReactTable,
} from '@tanstack/react-table'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import { Skeleton } from '@/common/ui/skeleton'
import { Button } from '@/common/ui/button'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Input } from '@/common/ui/input'
import { ScrollArea, ScrollBar } from '@/common/ui/scroll-area'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import {
	DoubleArrowLeftIcon,
	DoubleArrowRightIcon,
} from '@radix-ui/react-icons'
import { ChevronLeftIcon, ChevronRightIcon, Search } from 'lucide-react'
import { useQueryGetChannelPlayers } from '@/modules/dashboard/api/hooks/useQueryGetChannelPlayers'
import type {
	ChannelPlayerItem,
	ChannelPlayerAverages,
} from '@/modules/dashboard/type'

interface Props {
	channelId: string
}

const fmt = (n: number) =>
	new Intl.NumberFormat('th-TH', {
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	}).format(n)

const fmtAvg = (n: number) =>
	new Intl.NumberFormat('th-TH', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(n)

const columnHelper = createColumnHelper<ChannelPlayerItem>()

const PAGE_SIZES = [10, 20, 50]

function AverageCards({ averages }: { averages: ChannelPlayerAverages }) {
	return (
		<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
			<Card className="p-4">
				<p className="text-xs text-muted-foreground">ฝากต่อคนเฉลี่ย</p>
				<p className="mt-1 text-lg font-bold">
					{fmtAvg(averages.avgTotalDepositAmount)}
				</p>
			</Card>
			<Card className="p-4">
				<p className="text-xs text-muted-foreground">ถอนต่อคนเฉลี่ย</p>
				<p className="mt-1 text-lg font-bold">
					{fmtAvg(averages.avgTotalWithdrawAmount)}
				</p>
			</Card>
			<Card className="p-4">
				<p className="text-xs text-muted-foreground">
					เทิร์นโอเวอร์ต่อคนเฉลี่ย
				</p>
				<p className="mt-1 text-lg font-bold">
					{fmtAvg(averages.avgTotalTurnoverAmount)}
				</p>
			</Card>
			<Card className="p-4">
				<p className="text-xs text-muted-foreground">กำไรต่อคนเฉลี่ย</p>
				<p
					className={`mt-1 text-lg font-bold ${averages.avgProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}
				>
					{fmtAvg(averages.avgProfit)}
				</p>
			</Card>
			<Card className="p-4">
				<p className="text-xs text-muted-foreground">ฝากต่อครั้งเฉลี่ย</p>
				<p className="mt-1 text-lg font-bold">
					{fmtAvg(averages.avgDepositPerPlayer)}
				</p>
			</Card>
			<Card className="p-4">
				<p className="text-xs text-muted-foreground">อัตราฝากแรก</p>
				<p className="mt-1 text-lg font-bold">
					{fmtAvg(averages.depositRate)}%
				</p>
			</Card>
		</div>
	)
}

const columns = [
	columnHelper.accessor('phone', {
		header: 'เบอร์โทร',
		cell: (info) => (
			<span className="font-mono text-sm">{info.getValue()}</span>
		),
	}),
	columnHelper.accessor('username', {
		header: 'ชื่อผู้ใช้',
		cell: (info) => info.getValue() ?? '-',
	}),
	columnHelper.accessor('createdAt', {
		header: 'วันที่สมัคร',
		cell: (info) => {
			const v = info.getValue()
			if (!v) return '-'
			const d = new Date(v)
			return isNaN(d.getTime()) ? '-' : format(d, 'dd MMM yy', { locale: th })
		},
	}),
	columnHelper.accessor('firstDepositAt', {
		header: 'ฝากครั้งแรก',
		cell: (info) => {
			const v = info.getValue()
			if (!v)
				return <span className="text-red-600 dark:text-red-400">ยังไม่ฝาก</span>
			const d = new Date(v)
			return isNaN(d.getTime()) ? '-' : format(d, 'dd MMM yy', { locale: th })
		},
	}),
	columnHelper.accessor('totalDepositAmount', {
		header: 'ฝากรวม',
		cell: (info) => (
			<span className="text-green-600 dark:text-green-400">
				{fmt(info.getValue())}
			</span>
		),
	}),
	columnHelper.accessor('totalDepositTime', {
		header: 'ครั้งฝาก',
		cell: (info) => fmt(info.getValue()),
	}),
	columnHelper.accessor('totalWithdrawAmount', {
		header: 'ถอนรวม',
		cell: (info) => (
			<span className="text-red-600 dark:text-red-400">
				{fmt(info.getValue())}
			</span>
		),
	}),
	columnHelper.accessor('totalWithdrawTime', {
		header: 'ครั้งถอน',
		cell: (info) => fmt(info.getValue()),
	}),
	columnHelper.accessor('totalTurnoverAmount', {
		header: 'เทิร์นโอเวอร์',
		cell: (info) => fmt(info.getValue()),
	}),
	columnHelper.accessor('profit', {
		header: 'กำไร',
		cell: (info) => {
			const v = info.getValue()
			return (
				<span
					className={
						v >= 0
							? 'font-semibold text-green-600 dark:text-green-400'
							: 'font-semibold text-red-600 dark:text-red-400'
					}
				>
					{fmt(v)}
				</span>
			)
		},
	}),
	columnHelper.accessor('avgDepositAmount', {
		header: 'ยอดฝากเฉลี่ย',
		cell: (info) => fmtAvg(info.getValue()),
	}),
]

const ChannelPlayerTable: React.FC<Props> = ({ channelId }) => {
	const navigate = useNavigate()
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(20)
	const [search, setSearch] = useState('')
	const [searchInput, setSearchInput] = useState('')

	const skip = (page - 1) * pageSize

	const { data, isLoading } = useQueryGetChannelPlayers(channelId, {
		skip,
		take: pageSize,
		search: search || undefined,
	})

	const total = data?.total ?? 0
	const pageCount = Math.ceil(total / pageSize)

	const table = useReactTable({
		data: data?.data ?? [],
		columns,
		getCoreRowModel: getCoreRowModel(),
		manualPagination: true,
		pageCount,
	})

	const handleSearch = () => {
		setSearch(searchInput)
		setPage(1)
	}

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center gap-3">
				<div className="flex items-center gap-2">
					<Input
						placeholder="ค้นหาเบอร์โทร / ชื่อผู้ใช้"
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
						className="w-64"
					/>
					<Button variant="outline" size="sm" onClick={handleSearch}>
						<Search className="h-4 w-4" />
					</Button>
				</div>
				<span className="text-sm text-muted-foreground">
					ทั้งหมด {fmt(total)} คน
				</span>
			</div>

			{data?.averages && <AverageCards averages={data.averages} />}

			{isLoading ? (
				<div className="space-y-2">
					{Array.from({ length: 5 }).map((_, i) => (
						<Skeleton key={i} className="h-10 w-full" />
					))}
				</div>
			) : !data?.data.length ? (
				<div className="flex h-32 items-center justify-center rounded-md border bg-card">
					<p className="text-muted-foreground">ไม่พบข้อมูลผู้เล่น</p>
				</div>
			) : (
				<ScrollArea className="rounded-md border">
					<table className="min-w-full divide-y divide-border">
						<thead className="bg-muted">
							{table.getHeaderGroups().map((hg) => (
								<tr key={hg.id}>
									{hg.headers.map((h) => (
										<th
											key={h.id}
											className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
										>
											{flexRender(h.column.columnDef.header, h.getContext())}
										</th>
									))}
								</tr>
							))}
						</thead>
						<tbody className="divide-y divide-border bg-card">
							{table.getRowModel().rows.map((row) => (
								<tr
									key={row.id}
									className="cursor-pointer hover:bg-muted/50"
									onClick={() =>
										navigate(`/operator/player/${row.original.id}`)
									}
								>
									{row.getVisibleCells().map((cell) => (
										<td
											key={cell.id}
											className="whitespace-nowrap px-4 py-3 text-sm text-card-foreground"
										>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext(),
											)}
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
					<ScrollBar orientation="horizontal" />
				</ScrollArea>
			)}

			{total > 0 && (
				<div className="flex flex-col items-center justify-between gap-2 sm:flex-row">
					<div className="flex items-center gap-2">
						<p className="text-sm text-muted-foreground">จำนวนแถวต่อหน้า</p>
						<Select
							value={`${pageSize}`}
							onValueChange={(v) => {
								setPageSize(Number(v))
								setPage(1)
							}}
						>
							<SelectTrigger className="h-8 w-[70px]">
								<SelectValue />
							</SelectTrigger>
							<SelectContent side="top">
								{PAGE_SIZES.map((s) => (
									<SelectItem key={s} value={`${s}`}>
										{s}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className="flex items-center gap-4">
						<span className="text-sm text-muted-foreground">
							หน้า {page} จาก {pageCount}
						</span>
						<div className="flex items-center gap-1">
							<Button
								variant="outline"
								size="icon"
								className="h-8 w-8"
								onClick={() => setPage(1)}
								disabled={page === 1}
							>
								<DoubleArrowLeftIcon className="h-4 w-4" />
							</Button>
							<Button
								variant="outline"
								size="icon"
								className="h-8 w-8"
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								disabled={page === 1}
							>
								<ChevronLeftIcon className="h-4 w-4" />
							</Button>
							<Button
								variant="outline"
								size="icon"
								className="h-8 w-8"
								onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
								disabled={page >= pageCount}
							>
								<ChevronRightIcon className="h-4 w-4" />
							</Button>
							<Button
								variant="outline"
								size="icon"
								className="h-8 w-8"
								onClick={() => setPage(pageCount)}
								disabled={page >= pageCount}
							>
								<DoubleArrowRightIcon className="h-4 w-4" />
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

function ChannelPlayerTableSection({ channelId }: { channelId: string }) {
	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-lg">รายชื่อผู้เล่น</CardTitle>
			</CardHeader>
			<CardContent>
				<ChannelPlayerTable channelId={channelId} />
			</CardContent>
		</Card>
	)
}

export default ChannelPlayerTableSection
